import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import heroImage from '../assets/hero.jpg';

interface HeroProps {
  theme?: 'dark' | 'light';
}

const Hero: React.FC<HeroProps> = ({ theme = 'dark' }) => {
  const isLight = theme === 'light';

  return (
    <section id="home" className={`relative h-screen flex items-center justify-center overflow-hidden ${isLight ? 'bg-gray-100' : 'bg-primary'}`}>
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="Luxury Cabinetry"
          className="w-full h-full object-cover"
        />
        {/* Overlay */}
        <div className={`absolute inset-0 ${isLight ? 'bg-gray-200/90' : 'bg-primary/90'}`} />
        
        {/* Subtle Radial Gradient */}
        <div className={`absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] ${isLight ? 'from-white/40 via-transparent to-gray-200/40' : 'from-white/5 via-transparent to-primary/40'} opacity-50`} />
      </div>

      {/* Geometric Lines Overlay */}
      <div className="absolute inset-0 z-10 pointer-events-none opacity-20">
        <div className={`absolute top-0 left-1/4 w-px h-full ${isLight ? 'bg-primary/20' : 'bg-white/30'}`} />
        <div className={`absolute top-0 right-1/4 w-px h-full ${isLight ? 'bg-primary/20' : 'bg-white/30'}`} />
        <div className={`absolute top-1/3 left-0 w-full h-px ${isLight ? 'bg-primary/20' : 'bg-white/30'}`} />
        <div className={`absolute bottom-1/3 left-0 w-full h-px ${isLight ? 'bg-primary/20' : 'bg-white/30'}`} />
      </div>

      {/* Content */}
      <div className={`container-custom relative z-20 text-center ${isLight ? 'text-primary' : 'text-white'}`}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2, ease: "easeOut" }}
        >
          <div className="inline-block mb-6 md:mb-8">
            <h2 className="text-xs md:text-base uppercase tracking-[0.3em] text-accent font-bold mb-4">
              Organizing Your Space
            </h2>
            <div className="h-px w-full bg-accent/80"></div>
          </div>
          
          <h1 className="text-3xl md:text-6xl lg:text-7xl font-bold mb-8 md:mb-12 leading-tight tracking-tight">
            Architectural Cabinetry <br />
            <span className="font-light block mt-2 md:mt-4">Designed for Elevated Living</span>
          </h1>
          
          <p className={`max-w-2xl mx-auto text-base md:text-2xl mb-10 md:mb-16 font-light leading-relaxed ${isLight ? 'text-primary/80' : 'text-gray-100'}`}>
            Custom kitchens, bathrooms and closets crafted with precision and intention.
          </p>
          
          <div className="flex flex-col sm:flex-row justify-center gap-4 md:gap-6">
            <a
              href="#contact"
              className="bg-accent hover:bg-orange-600 text-white px-10 py-5 uppercase tracking-[0.2em] text-sm font-bold transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-orange-500/20 w-full sm:w-auto"
            >
              Schedule Your Design Consultation
            </a>
            <a
              href="#projects"
              className={`border-2 px-10 py-5 uppercase tracking-[0.2em] text-sm font-bold transition-all flex items-center justify-center gap-3 group w-full sm:w-auto ${
                isLight 
                  ? 'border-primary text-primary hover:bg-primary hover:text-white' 
                  : 'border-white text-white hover:bg-white hover:text-primary'
              }`}
            >
              View Our Work
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
